import { matchPath } from 'react-router-dom';
import { formatDocumentTitle } from './documentTitle.js';
import {
  ADMIN_ROUTE_PREFIX,
  ADMIN_ROUTE_SEO,
  PUBLIC_ROUTE_SEO,
  SEO_DEFAULTS,
  SITE_URL,
} from '../constants/seo.js';

const MANAGED = 'data-seo-managed';

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
  if (!base) return pathOrUrl;
  return `${base}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
}

function upsertMeta(attr, key, content) {
  const selector = `meta[${attr}="${key}"][${MANAGED}]`;
  let el = document.head.querySelector(selector);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  const selector = `link[rel="${rel}"][${MANAGED}]`;
  let el = document.head.querySelector(selector);
  if (!href) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(id, payload) {
  let el = document.getElementById(id);
  if (!payload) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    el.setAttribute(MANAGED, '');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
}

export function routeSeoForPath(pathname) {
  for (const route of PUBLIC_ROUTE_SEO) {
    if (matchPath({ path: route.path, end: true }, pathname)) {
      return route;
    }
  }
  return null;
}

export function adminRouteSeoForPath(pathname) {
  for (const route of ADMIN_ROUTE_SEO) {
    if (matchPath({ path: route.path, end: true }, pathname)) {
      return route;
    }
  }
  return null;
}

export function isAdminPath(pathname) {
  return pathname.startsWith(ADMIN_ROUTE_PREFIX);
}

/**
 * @param {{
 *   pathname: string,
 *   title?: string,
 *   description?: string,
 *   image?: string,
 *   noIndex?: boolean,
 *   type?: string,
 * }} options
 */
export function applyPageSeo(options) {
  const { pathname, title, description, image, noIndex, type = 'website' } = options;
  const route = routeSeoForPath(pathname);
  const adminRoute = adminRouteSeoForPath(pathname);
  const admin = isAdminPath(pathname);

  const pageTitle =
    title ||
    route?.title ||
    adminRoute?.title ||
    (admin ? 'Panel administrativo' : SEO_DEFAULTS.siteName);
  const fullTitle = formatDocumentTitle(pageTitle);
  const desc =
    (description || route?.description || SEO_DEFAULTS.defaultDescription).slice(0, 320);
  const canonical = absoluteUrl(pathname);
  const ogImage = absoluteUrl(image || SEO_DEFAULTS.ogImagePath);
  const robots = noIndex ?? admin ? 'noindex, nofollow' : 'index, follow';

  document.title = fullTitle;
  document.documentElement.lang = 'es';

  upsertMeta('name', 'description', desc);
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'keywords', SEO_DEFAULTS.defaultKeywords);
  upsertMeta('name', 'author', SEO_DEFAULTS.siteName);
  upsertMeta('name', 'theme-color', '#0a118a');

  upsertLink('canonical', canonical);

  upsertMeta('property', 'og:locale', SEO_DEFAULTS.locale);
  upsertMeta('property', 'og:site_name', SEO_DEFAULTS.siteName);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:title', fullTitle);
  upsertMeta('property', 'og:description', desc);
  upsertMeta('property', 'og:url', canonical);
  if (ogImage) upsertMeta('property', 'og:image', ogImage);

  upsertMeta('name', 'twitter:card', SEO_DEFAULTS.twitterCard);
  upsertMeta('name', 'twitter:title', fullTitle);
  upsertMeta('name', 'twitter:description', desc);
  if (ogImage) upsertMeta('name', 'twitter:image', ogImage);

  if (!admin && pathname === '/') {
    upsertJsonLd('seo-org-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: SEO_DEFAULTS.legalName,
      alternateName: SEO_DEFAULTS.siteName,
      url: SITE_URL || canonical,
      logo: ogImage,
    });
  } else {
    upsertJsonLd('seo-org-jsonld', null);
  }

  if (!admin && route && !pathname.includes(':')) {
    upsertJsonLd('seo-webpage-jsonld', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: fullTitle,
      description: desc,
      url: canonical,
      isPartOf: { '@type': 'WebSite', name: SEO_DEFAULTS.siteName, url: SITE_URL || canonical },
    });
  } else {
    upsertJsonLd('seo-webpage-jsonld', null);
  }
}

export function stripHtmlToText(html, maxLen = 160) {
  if (!html) return '';
  const text = String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > maxLen ? `${text.slice(0, maxLen - 1)}…` : text;
}
